/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  Lifecycle,
  ListAgentsRequest,
  ListAgentsSecurity,
  NameOrder,
} from "../models/operations/listagents.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AgentsInfiniteQueryData,
  AgentsPageParams,
  AgentsQueryData,
  buildAgentsInfiniteQuery,
  buildAgentsQuery,
  prefetchAgents,
  prefetchAgentsInfinite,
  queryKeyAgents,
  queryKeyAgentsInfinite,
} from "./agents.core.js";
export {
  type AgentsInfiniteQueryData,
  type AgentsPageParams,
  type AgentsQueryData,
  buildAgentsInfiniteQuery,
  buildAgentsQuery,
  prefetchAgents,
  prefetchAgentsInfinite,
  queryKeyAgents,
  queryKeyAgentsInfinite,
};

export type AgentsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * list agents
 */
export function useAgents(
  request?: ListAgentsRequest | undefined,
  security?: ListAgentsSecurity | undefined,
  options?: QueryHookOptions<AgentsQueryData, AgentsQueryError>,
): UseQueryResult<AgentsQueryData, AgentsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAgentsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list agents
 */
export function useAgentsSuspense(
  request?: ListAgentsRequest | undefined,
  security?: ListAgentsSecurity | undefined,
  options?: SuspenseQueryHookOptions<AgentsQueryData, AgentsQueryError>,
): UseSuspenseQueryResult<AgentsQueryData, AgentsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAgentsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list agents
 */
export function useAgentsInfinite(
  request?: ListAgentsRequest | undefined,
  security?: ListAgentsSecurity | undefined,
  options?: InfiniteQueryHookOptions<AgentsInfiniteQueryData, AgentsQueryError>,
): UseInfiniteQueryResult<
  InfiniteData<AgentsInfiniteQueryData, AgentsPageParams>,
  AgentsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    AgentsInfiniteQueryData,
    AgentsQueryError,
    InfiniteData<AgentsInfiniteQueryData, AgentsPageParams>,
    QueryKey,
    AgentsPageParams
  >({
    ...buildAgentsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * list agents
 */
export function useAgentsInfiniteSuspense(
  request?: ListAgentsRequest | undefined,
  security?: ListAgentsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    AgentsInfiniteQueryData,
    AgentsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<AgentsInfiniteQueryData, AgentsPageParams>,
  AgentsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    AgentsInfiniteQueryData,
    AgentsQueryError,
    InfiniteData<AgentsInfiniteQueryData, AgentsPageParams>,
    QueryKey,
    AgentsPageParams
  >({
    ...buildAgentsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setAgentsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      search?: string | undefined;
      nameOrder?: NameOrder | undefined;
      lifecycle?: Array<Lifecycle> | undefined;
      ownerUserIds?: Array<string> | undefined;
      registeredAfter?: Date | undefined;
      registeredBefore?: Date | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: AgentsQueryData,
): AgentsQueryData | undefined {
  const key = queryKeyAgents(...queryKeyBase);

  return client.setQueryData<AgentsQueryData>(key, data);
}

export function invalidateAgents(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      search?: string | undefined;
      nameOrder?: NameOrder | undefined;
      lifecycle?: Array<Lifecycle> | undefined;
      ownerUserIds?: Array<string> | undefined;
      registeredAfter?: Date | undefined;
      registeredBefore?: Date | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "agents", "list", ...queryKeyBase],
  });
}

export function invalidateAllAgents(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "agents", "list"],
  });
}
